import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import StoryTellingChat from "./StoryTellingChat";
import MessageInput from "./MessageInput";
import { Message } from "./Message";
import Spinner from "./Spinner";
import MindfulPausePrompt from "./MindfulPausePrompt";
import LanguageLearningPrompts from './LanguageLearningPrompts';
import World from './World';
import ParentMemoryChat from './ParentMemoryChat';
import PastChatsSidebar from './PastChatsSidebar';

const Chat = (props) => {
  const [messages, setMessages] = useState(props.chat.messages);
  const [input, setInput] = useState(props.prefilledMessage || "");
  const [imageFile, setImageFile] = useState(null);
  const [conversationStarters, setConversationStarters] = useState(props.conversationStarters || []);
  const [isStorySubmitted, setIsStorySubmitted] = useState(
    props.chat.chatType === "story_telling" && !!props.chat.chatStory
  );
  const [isLoading, setIsLoading] = useState(false);
  const [answerReady, setAnswerReady] = useState(false);
  const [pendingAnswer, setPendingAnswer] = useState(null);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [subject, setSubject] = useState(props.initialSubject || props.chat.subject || null);
  const [targetLanguage, setTargetLanguage] = useState(null);
  const [learningMode, setLearningMode] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true); // Default open on desktop
  const [startersOpen, setStartersOpen] = useState(false); // Conversation starters collapsed by default
  const messagesEndRef = useRef(null);
  const sentPrefilledRef = useRef(false);
  const kidName = props.chatOwner?.first_name || props.user?.first_name;
  const isStoryOwner = props.chat.userId === props.user.id;
  const isParentAccount = props.user.accountType === 'parent';
  const canPremiumStoryActions = !props.user.freemium && (isStoryOwner || isParentAccount);
  const isStoryChat = props.chat.chatType === "story_telling";

  // Helper function to humanize subject names (replace underscores with spaces and capitalize)
  const humanizeSubject = (subjectName) => {
    if (!subjectName) return '';
    return subjectName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Auto-submit prefilled message once after mount
  useEffect(() => {
    if (
      props.prefilledMessage &&
      !sentPrefilledRef.current &&
      props.prefilledMessage.trim().length > 0
    ) {
      // If there are no messages yet, send as initial; otherwise treat as follow-up
      if (messages.length === 0) {
        handleFollowUpClick(props.prefilledMessage);
      } else {
        handleFollowUpClick(props.prefilledMessage);
      }
      sentPrefilledRef.current = true;
    }
  }, [props.prefilledMessage]);

  // Automatically focus input when a prefilled message is provided
  useEffect(() => {
    if (props.prefilledMessage) {
      const inputElement = document.querySelector('textarea, input[type="text"]');
      inputElement?.focus();
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollTo({
      top: messagesEndRef.current.scrollHeight,
      behavior: "smooth"
    });
  };

  // Only auto-scroll on initial load or when user sends a message
  // Don't auto-scroll when AI responds (annoying for long responses)
  useEffect(() => {
    // Only scroll if this is the first message or if messages array is very small
    // This prevents auto-scroll after AI responses
    if (messages.length <= 1) {
      scrollToBottom();
    }
  }, [messages]);

  const handleFileChange = (file) => {
    setImageFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let uploadedImageURL = null;
    let uploadedImageId = null;

    if (imageFile) {
      const formData = new FormData();
      formData.append("chat_image[role]", "user");
      formData.append("chat_image[image]", imageFile);

      try {
        const response = await axios.post(
          `/chats/${props.chat.id}/chat_images`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${props.formToken}`,
              "Content-Type": "multipart/form-data",
              Accept: "application/json",
            },
          }
        );
        uploadedImageURL = response.data.imageURL;
        uploadedImageId = response.data.imageId;
      } catch (error) {
        console.error("Image upload failed:", error);
        return;
      }
    }

    const userMessage = {
      role: "user",
      content: input,
      image_url: uploadedImageURL,
      image_id: uploadedImageId,
    };

    setMessages([...messages, userMessage]);
    setInput("");
    setImageFile(null);
    setIsLoading(true);
    setAnswerReady(false);
    setPendingAnswer(null);
    setLoadingStartTime(Date.now());

    try {
      const response = await axios.post(
        `/api/v1/chats/${props.chat.id}/send_message`,
        {
          content: input,
          image_id: uploadedImageId,
          subject: subject || "anything",
          ...(props.chat.chatType === 'language_learning' && {
            language: targetLanguage?.code
          })
        },
        {
          headers: {
            Authorization: `Bearer ${props.formToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const assistantMessage = {
        role: "assistant",
        id: response.data.id,
        audioUrl: response.data.audio_url,
        content: response.data.content,
        confidence: response.data.confidence,
        image_prompts: response.data.image_prompts || [],
        equations: response.data.equations || [],
        follow_up: response.data.follow_up || '',
        map_focus: response.data.map_focus || null,
      };

      // Calculate if minimum time has elapsed (10 seconds)
      const elapsedTime = Date.now() - loadingStartTime;
      const minimumDelay = 10000; // 10 seconds

      if (elapsedTime >= minimumDelay) {
        // Show answer immediately
        setMessages((prevMessages) => [...prevMessages, assistantMessage]);
        setIsLoading(false);
      } else {
        // Wait for minimum delay, then show "Answer Ready" button
        const remainingTime = minimumDelay - elapsedTime;
        setPendingAnswer(assistantMessage);

        setTimeout(() => {
          setAnswerReady(true);
        }, remainingTime);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setIsLoading(false);
    }
  };

  const handleAnswerReady = () => {
    if (pendingAnswer) {
      setMessages((prevMessages) => [...prevMessages, pendingAnswer]);
      setPendingAnswer(null);
      setAnswerReady(false);
      setIsLoading(false);
    }
  };

  const regularSubjects = ["Math", "Science", "History", "Geography", "Language Arts", "AI Learning", "Anything"];

  const languageModes = [
    { id: 'conversation', label: 'Have a Conversation', icon: '💭' },
    { id: 'vocabulary', label: 'Learn New Words', icon: '📚' },
    { id: 'grammar', label: 'Practice Grammar', icon: '✏️' },
    { id: 'pronunciation', label: 'Pronunciation Help', icon: '🗣️' },
  ];

  const languages = [
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'nl', name: 'Dutch' },
  ];

  const isNewChat = messages.length === 0;

  const handleFollowUpClick = async (question) => {
    if (isLoading) return;

    const userMessage = {
      role: "user",
      content: question,
    };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput('');
    setImageFile(null);
    setIsLoading(true);
    setAnswerReady(false);
    setPendingAnswer(null);
    setLoadingStartTime(Date.now());

    try {
      const response = await axios.post(
        `/api/v1/chats/${props.chat.id}/send_message`,
        {
          content: question,
          subject: subject || "anything",
          ...(props.chat.chatType === 'language_learning' && {
            language: targetLanguage?.code
          })
        },
        {
          headers: {
            Authorization: `Bearer ${props.formToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const assistantMessage = {
        role: "assistant",
        id: response.data.id,
        audioUrl: response.data.audio_url,
        content: response.data.content,
        confidence: response.data.confidence,
        image_prompts: response.data.image_prompts || [],
        equations: response.data.equations || [],
        follow_up: response.data.follow_up || [],
        map_focus: response.data.map_focus || null,
      };

      // Calculate if minimum time has elapsed (10 seconds)
      const elapsedTime = Date.now() - loadingStartTime;
      const minimumDelay = 10000; // 10 seconds

      if (elapsedTime >= minimumDelay) {
        // Show answer immediately
        setMessages((prevMessages) => [...prevMessages, assistantMessage]);
        setIsLoading(false);
      } else {
        // Wait for minimum delay, then show "Answer Ready" button
        const remainingTime = minimumDelay - elapsedTime;
        setPendingAnswer(assistantMessage);

        setTimeout(() => {
          setAnswerReady(true);
        }, remainingTime);
      }

    } catch (error) {
      console.error("Error sending follow-up message:", error);
      const errorMessage = {
        role: "system",
        content: "Sorry, I couldn't process that request. Please try again.",
        id: Date.now()
      };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleSaveMemory = async (memoryData, memoryType) => {
    try {
      const response = await axios.post(
        `/user_ai_memories/create_from_chat`,
        {
          memory_data: memoryData,
          memory_type: memoryType,
          user_id: props.childId
        },
        {
          headers: {
            Authorization: `Bearer ${props.formToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error saving memory:", error);
      return { success: false, error: "Failed to save memory" };
    }
  };

  const handleStarterClick = async (starter) => {
    if (!starter) return;

    // Track click for analytics
    try {
      await axios.post(`/api/v1/conversation_starters/${starter.id}/click`, {}, {
        headers: {
          Authorization: `Bearer ${props.formToken}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
    } catch (error) {
      console.error("Error tracking conversation starter click:", error);
    }

    // Remove the starter from local state so it doesn't appear again
    setConversationStarters((prev) => prev.filter((s) => s.id !== starter.id));

    // Send the starter content as a follow-up message
    handleFollowUpClick(starter.content);
  };

  const handleTurnIntoQuiz = async () => {
    if (isLoading || messages.length < 2) return;

    try {
      // Show loading state
      setIsLoading(true);

      // Call the chat-to-quiz conversion endpoint
      const response = await axios.post(
        `/api/v1/chats/${props.chat.id}/convert_to_quiz`,
        {},
        {
          headers: {
            Authorization: `Bearer ${props.formToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.data.success) {
        // Redirect to quiz generator with prefilled prompt
        window.location.href = response.data.redirect_url;
      } else {
        console.error("Failed to convert chat to quiz:", response.data.error);
        alert("Sorry, there was an error converting your chat to a quiz. Please try again.");
      }
    } catch (error) {
      console.error("Error converting chat to quiz:", error);
      alert("Sorry, there was an error converting your chat to a quiz. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`${isStoryChat ? "min-h-screen overflow-y-auto" : "h-screen"} flex relative`}>
      {/* Past Chats Sidebar - only show if we have pastChats and subject */}
      {props.pastChats && props.pastChats.length > 0 && subject && (
        <PastChatsSidebar
          pastChats={props.pastChats}
          currentChatId={props.chat.id}
          subject={subject || props.initialSubject}
          chatType={props.chat.chatType}
          isOpen={sidebarOpen}
          onToggle={setSidebarOpen}
        />
      )}


      {/* Main Content */}
      <div className={`flex-1 ${isStoryChat ? "" : "overflow-hidden"}`}>
        <div className={`mx-auto py-4 ${isStoryChat ? "min-h-screen" : "h-screen"} flex flex-col px-4`}>
          {subject && !isStoryChat && props.chat.chatType !== 'language_learning' && (
            <div className="flex justify-end mb-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
                Topic: {humanizeSubject(subject)}
              </span>
            </div>
          )}
          {props.chat.chatType === 'parent_memory' ? (
            <ParentMemoryChat
              chat={props.chat}
              formToken={props.formToken}
              childId={props.childId}
              onSaveMemory={handleSaveMemory}
            />
          ) : props.chat.chatType === 'map_explorer' ? (
            <World
              apiKey={process.env.GOOGLE_MAPS_API_KEY}
              formToken={props.formToken}
              chat={props.chat}
            />
          ) : (
        <div>
          {isNewChat && props.chat.chatType === 'language_learning' ? (
            <div className="mb-4 space-y-6">
              {!targetLanguage && (
                <div className="bg-white p-6 rounded-lg shadow">
                  <h2 className="text-xl font-bold mb-4">What language would you like to learn?</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => setTargetLanguage(lang)}
                        className="p-4 text-lg rounded-lg border-2 hover:border-blue-500 hover:bg-blue-50 transition-colors
                                 flex items-center justify-center space-x-2"
                      >
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {targetLanguage && !learningMode && (
                <div className="bg-white p-6 rounded-lg shadow">
                  <h2 className="text-xl font-bold mb-4">What would you like to do?</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {languageModes.map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => setLearningMode(mode)}
                        className="p-6 text-lg rounded-lg border-2 hover:border-blue-500 hover:bg-blue-50 transition-colors
                                 flex items-center justify-center space-x-3"
                      >
                        <span className="text-2xl">{mode.icon}</span>
                        <span>{mode.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {targetLanguage && learningMode && (
                <LanguageLearningPrompts
                  language={targetLanguage}
                  mode={learningMode}
                  onPromptSelect={(prompt) => setInput(prompt)}
                />
              )}
            </div>
          ) : isNewChat && !isStorySubmitted && props.chat.chatType !== 'language_learning' && props.chat.chatType !== 'story_telling' && !subject ? (
            <div className="mb-4">
              <h2 className="text-xl font-bold mb-2 bg-white p-2 rounded-lg">Choose a subject:</h2>
              <div className="flex flex-wrap gap-2 p-2 bg-white rounded-lg">
                {regularSubjects.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => setSubject(sub)}
                    className={`px-4 py-2 rounded ${
                      subject === sub
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-800"
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {isNewChat && conversationStarters.length > 0 && props.chat.chatType !== 'story_telling' && (
            <div className="mb-4 bg-white p-3 rounded-lg">
              <button
                onClick={() => setStartersOpen(!startersOpen)}
                className="flex items-center text-indigo-600 hover:text-indigo-800
                          bg-indigo-50 hover:bg-indigo-100 rounded-full px-4 py-2
                          transition-all duration-200 text-sm font-medium"
              >
                {startersOpen ? <FaChevronUp className="mr-2" /> : <FaChevronDown className="mr-2" />}
                Need an idea? Try one of these!
              </button>
              {startersOpen && (
                <div className="flex flex-wrap gap-2 mt-3 pl-4">
                  {conversationStarters.map((starter) => (
                    <button
                      key={starter.id}
                      onClick={() => handleStarterClick(starter)}
                      className="px-4 py-2 rounded bg-blue-50 hover:bg-blue-100 text-gray-800 transition-colors"
                    >
                      {starter.content}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!isStoryChat && (
            <div
              ref={messagesEndRef}
              className="mb-4 overflow-y-auto"
              style={{ maxHeight: "calc(100vh - 250px)" }}
            >
              {props.chat.chatType === 'language_learning' && isNewChat && !isStorySubmitted ? (
                <div className="text-center mt-4 text-gray-500 bg-white p-4 rounded-lg">
                  {!targetLanguage
                    ? "Choose a language to start learning!"
                    : !learningMode
                      ? "Select what you'd like to practice!"
                      : "Pick a prompt to start chatting or type your own!"}
                </div>
              ) : (
                messages.map((message, index) => (
                  <Message
                    key={`${index}-messages`}
                    message={{ ...message, chat_type: props.chat.chatType }}
                    user={props.user}
                    onFollowUpClick={handleFollowUpClick}
                    formToken={props.formToken}
                    chatId={props.chat.id}
                    isLastMessage={index === messages.length - 1}
                    canTurnIntoQuiz={
                      !props.readOnly &&
                      messages.length >= 2 &&
                      !isNewChat &&
                      message.role === 'assistant'
                    }
                    onTurnIntoQuiz={handleTurnIntoQuiz}
                    isLoading={isLoading}
                  />
                ))
              )}
              {isLoading && (
                <MindfulPausePrompt
                  onAnswerReady={handleAnswerReady}
                  showAnswerButton={answerReady}
                />
              )}
            </div>
          )}

          {!props.readOnly &&
          props.chat.chatType !== "story_telling" &&
          !isStorySubmitted && (
            <MessageInput
              handleSubmit={handleSubmit}
              input={input}
              setInput={setInput}
              handleFileChange={handleFileChange}
              user={props.user}
              showWork={props.chat.chatType === "show_work"}
              messageCount={messages.length}
              disabled={isNewChat && (
                props.chat.chatType === 'language_learning'
                  ? !targetLanguage || !learningMode
                  : !subject
              )}
            />
          )}

          {props.chat.chatType === "story_telling" && (
            <StoryTellingChat
              formToken={props.formToken}
              onSaveChat={() => setIsStorySubmitted(true)}
              chatId={props.chat.id}
              chatStory={props.chat.chatStory}
              kidName={kidName}
              canGenerateAudio={canPremiumStoryActions}
              canGenerateImages={canPremiumStoryActions}
              canTogglePublic={canPremiumStoryActions}
              readOnlyStory={!canPremiumStoryActions}
            />
          )}

          </div>
        )}
        </div>
      </div>
    </div>
  );
};

Chat.propTypes = {
  chatKey: PropTypes.number,
  name: PropTypes.string,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    first_name: PropTypes.string,
    freemium: PropTypes.bool,
    accountType: PropTypes.string,
  }).isRequired,
  chat: PropTypes.shape({
    id: PropTypes.number.isRequired,
    chatType: PropTypes.string.isRequired,
    messages: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        role: PropTypes.string.isRequired,
        content: PropTypes.string,
        image_url: PropTypes.string,
      })
    ).isRequired,
    chatStory: PropTypes.object,
  }).isRequired,
  initialSubject: PropTypes.string,
  prefilledMessage: PropTypes.string,
  conversationStarters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      content: PropTypes.string.isRequired,
    })
  ),
  chatOwner: PropTypes.shape({
    id: PropTypes.number,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    avatar: PropTypes.string,
  }),
  pastChats: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      subject: PropTypes.string,
      created_at: PropTypes.string.isRequired,
      updated_at: PropTypes.string,
      preview: PropTypes.string.isRequired,
    })
  ),
};

export default Chat;
